import { FC, ReactNode, useMemo, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Tooltip, Divider } from 'antd';
import {
  Draggable,
  DraggingStyle,
  NotDraggingStyle,
} from 'react-beautiful-dnd';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc/EditProcProvider';
import { useDeleteComponent } from '@/shared/api';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Row } from '@/shared/ui';
import { captureError } from '@/shared/utils';

type Props = {
  block: any;
  index: number;
  children: ReactNode;
};

export const ProcsComponentWrapper: FC<Props> = ({
  children,
  block,
  index,
}) => {
  const queryClient = useQueryClient();
  const { theme } = useThemeContext();
  const { selectedComponent, setSelectedComponent, selectedStep, proc } =
    useProcContext();

  const [isHovering, setIsHovering] = useState(false);

  const { mutate: deleteComponent } = useDeleteComponent();

  const isSelected = useMemo(
    () => block.id === selectedComponent?.id,
    [selectedComponent?.id],
  );

  const handleComponentSelect = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    component: any,
  ) => {
    e.stopPropagation();
    setSelectedComponent(component);
  };

  const handleComponentRemove = async (e: any) => {
    e.stopPropagation();
    try {
      deleteComponent(
        {
          pathParams: {
            procId: proc?.id!,
            stepId: selectedStep?.id!,
            componentId: block.id,
          },
        },
        {
          onSuccess() {
            queryClient.invalidateQueries({
              queryKey: ['procs', proc?.id],
            });
            setSelectedComponent(undefined);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'component_wrapper',
        action: 'submit',
      });
    }
  };

  const getItemStyle = (
    isDragging: boolean,
    draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
  ) => ({
    userSelect: 'none',
    opacity: isDragging ? 0.8 : 1,
    ...draggableStyle,
  });

  return (
    <Draggable key={block.id} draggableId={block.id} index={index}>
      {(provided, snapshot) => (
        <Wrapper
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onClick={(e) => handleComponentSelect(e, block)}
          style={{
            width: 'auto',
            border:
              isSelected || isHovering
                ? `1px solid ${themeConfig[theme].colors.primary}`
                : '1px solid transparent',
            background: snapshot.isDragging
              ? themeConfig[theme].colors.primary
              : 'transparent',
            cursor: snapshot.isDragging
              ? 'grabbing'
              : isSelected
                ? 'grab'
                : 'pointer',
            ...getItemStyle(snapshot.isDragging, provided.draggableProps.style),
          }}
        >
          <>
            {(isSelected || isHovering) && (
              <Row
                dataTestId="component-wrapper"
                gap={3}
                alignItems="center"
                style={{
                  position: 'absolute',
                  top: -23,
                  left: 2,
                  background: themeConfig[theme].colors.primary,
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                  paddingLeft: 5,
                  width: 'fit-content',
                  color: '#fff',
                  fontSize: 11,
                }}
              >
                {block.componentName}
                <Divider
                  type="vertical"
                  style={{
                    lineHeight: 2,
                    background: '#fff',
                    opacity: 0.5,
                    marginTop: 2,
                    marginLeft: 10,
                    marginRight: -3,
                  }}
                />
                <Tooltip title="Delete" mouseEnterDelay={0.5}>
                  <Button
                    data-testid="delete-component-button"
                    type="text"
                    shape="circle"
                    icon={
                      <DeleteOutlined style={{ color: '#fff', fontSize: 11 }} />
                    }
                    size="small"
                    onClick={handleComponentRemove}
                    style={{ marginTop: -2 }}
                  />
                </Tooltip>
              </Row>
            )}
            {/* used to disable interacting with component if it's not selected */}
            {!isSelected && <Overlay />}
            {children}
          </>
        </Wrapper>
      )}
    </Draggable>
  );
};

const Wrapper = styled.div`
  position: relative;
  cursor: pointer;
  margin: 2rem 1rem;
  padding: 8px 3px;
  border-radius: 4px;
  user-select: none;
  word-break: break-word;
`;

const Overlay = styled.div`
  z-index: 1;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;
